/**
 * Enriquecimiento por tema: índice de epígrafes + normativa concreta.
 *
 * Se aplica como overlay sobre los temarios base de `temariosNuevosCuerpos.ts`
 * mediante `enrichTopics()`. Mantener este archivo separado evita inflar el
 * archivo principal y permite añadir/corregir epígrafes sin tocar la estructura
 * (id, number, name, block) de los temarios oficiales.
 *
 * Fuentes:
 *  - IIPP    → CEAC 2026 (45 temas, estructura oficial vigente).
 *  - GC      → MAD 2024-2025 (25 temas, Resolución oficial).
 *  - C1      → MAD 2026 (47 temas, BOE 22-12-2025).
 *  - C2      → MAD 2026 (28 temas, BOE 22-12-2025).
 *
 * Reglas:
 *  - 4-7 epígrafes por tema (los preguntables en test).
 *  - Normativa real con artículos siempre que sea posible (sin inventar).
 */

import type { CuerpoTopic } from './temariosNuevosCuerpos';

export interface TopicEnrichment {
  index: string[];
  normativa: string[];
}

// ───────────────────────────── IIPP (45 temas CEAC 2026) ─────────────────────────────
export const ENRICHMENT_IIPP: Record<string, TopicEnrichment> = {
  'iipp-1': {
    index: [
      'Estructura y características de la CE de 1978',
      'Título Preliminar y valores superiores',
      'Derechos y deberes fundamentales (Tít. I)',
      'Garantías y suspensión de derechos',
      'Reforma constitucional',
    ],
    normativa: ['CE arts. 1-9', 'CE arts. 10-55', 'CE arts. 166-169'],
  },
  'iipp-2': {
    index: [
      'Principios constitucionales del Poder Judicial',
      'CGPJ: composición y funciones',
      'El Tribunal Supremo y la AN',
      'Ministerio Fiscal',
      'Independencia, unidad y exclusividad jurisdiccional',
    ],
    normativa: ['CE arts. 117-127', 'LO 6/1985 (LOPJ)', 'EOMF 50/1981'],
  },
  'iipp-3': {
    index: [
      'Composición y funciones del Gobierno',
      'Presidente, Vicepresidentes y Ministros',
      'Cese y responsabilidad',
      'Relaciones Gobierno–Cortes',
      'Administración y principios de actuación',
    ],
    normativa: ['CE arts. 97-107', 'Ley 50/1997 del Gobierno', 'Ley 40/2015 (LRJSP)'],
  },
  'iipp-4': {
    index: [
      'Principios de la organización territorial (CE Tít. VIII)',
      'Las Comunidades Autónomas: Estatutos',
      'Distribución competencial Estado-CCAA (arts. 148-149)',
      'Administración Local: provincia y municipio',
      'Competencias en materia penitenciaria (Cataluña, País Vasco)',
    ],
    normativa: ['CE arts. 137-158', 'Ley 7/1985 (LBRL)', 'EAC 6/2006'],
  },
  'iipp-5': {
    index: [
      'Antecedentes y proceso de integración europea',
      'Tratados constitutivos (TUE, TFUE)',
      'Instituciones: Parlamento, Consejo, Comisión, TJUE',
      'Fuentes del Derecho UE: derecho originario y derivado',
      'Principios: primacía y efecto directo',
    ],
    normativa: ['TUE', 'TFUE', 'Tratado de Lisboa 2007'],
  },
  'iipp-6': {
    index: [
      'El Ministerio del Interior: estructura',
      'Secretaría General de Instituciones Penitenciarias (SGIP)',
      'Centro Directivo: organización',
      'Servicios centrales y periféricos',
      'Organismo Autónomo Trabajo Penitenciario (OATPFE)',
    ],
    normativa: ['RD 734/2020 estructura M. Interior', 'RD 595/2018', 'LOGP arts. 79-80'],
  },
  'iipp-7': {
    index: [
      'Cuerpos y escalas de Instituciones Penitenciarias',
      'Personal facultativo, técnico y de vigilancia',
      'Provisión y selección',
      'Régimen jurídico aplicable',
      'Formación y promoción interna',
    ],
    normativa: ['LOGP arts. 80-81', 'RP arts. 277-296', 'TREBEP (RDL 5/2015)'],
  },
  'iipp-8': {
    index: [
      'Clases de personal: funcionario, laboral, eventual',
      'Adquisición y pérdida de la condición de funcionario',
      'Situaciones administrativas',
      'Derechos individuales y colectivos',
      'Régimen disciplinario',
    ],
    normativa: ['TREBEP arts. 8-13', 'TREBEP arts. 55-68', 'TREBEP arts. 85-92', 'TREBEP arts. 93-98'],
  },
  'iipp-9': {
    index: [
      'Principios del acceso al empleo público',
      'Sistemas selectivos: oposición, concurso-oposición',
      'Igualdad, mérito y capacidad',
      'Órganos de selección',
      'Toma de posesión',
    ],
    normativa: ['CE art. 23.2 y 103.3', 'TREBEP arts. 55-62', 'RD 364/1995'],
  },
  'iipp-10': {
    index: [
      'Tipos de contratos del sector público',
      'Procedimientos de adjudicación',
      'Capacidad y solvencia',
      'Ejecución, modificación y resolución',
      'Recurso especial en contratación',
    ],
    normativa: ['Ley 9/2017 (LCSP) arts. 12-25', 'LCSP arts. 116-187', 'LCSP arts. 195-246'],
  },
  'iipp-11': {
    index: [
      'Concepto y principios del voluntariado',
      'Derechos y deberes del voluntario',
      'La entidad de voluntariado',
      'Régimen jurídico de la actividad voluntaria',
      'Voluntariado en establecimientos penitenciarios',
    ],
    normativa: ['Ley 45/2015 de Voluntariado', 'RP art. 62'],
  },
  'iipp-12': {
    index: [
      'Concepto y elementos de las políticas públicas',
      'Ciclo: agenda, formulación, implantación, evaluación',
      'Indicadores y evaluación de resultados',
      'Políticas penitenciarias en España',
      'Reinserción y prevención de la reincidencia',
    ],
    normativa: ['CE art. 25.2', 'LOGP art. 1', 'Plan estratégico SGIP'],
  },
  'iipp-13': {
    index: [
      'Concepto y principios del Gobierno Abierto',
      'Transparencia, participación y colaboración',
      'Datos abiertos y rendición de cuentas',
      'Agenda 2030 y ODS',
      'Plan Nacional de Gobierno Abierto',
    ],
    normativa: ['Ley 19/2013 de Transparencia', 'OGP — Open Government Partnership'],
  },
  'iipp-14': {
    index: [
      'La Administración como sujeto: actividad administrativa',
      'Actividad de policía, fomento y servicio público',
      'Principios de actuación (eficacia, legalidad)',
      'Relaciones interadministrativas',
      'Sometimiento al Derecho',
    ],
    normativa: ['CE arts. 103-106', 'Ley 40/2015 (LRJSP) arts. 1-12'],
  },
  'iipp-15': {
    index: [
      'Concepto de fuente del Derecho Administrativo',
      'Constitución, ley, reglamento',
      'Tratados internacionales y Derecho UE',
      'Principios generales del Derecho',
      'Jerarquía normativa',
    ],
    normativa: ['CE art. 9.3', 'Código Civil arts. 1-7', 'Ley 50/1997'],
  },
  'iipp-16': {
    index: [
      'Régimen jurídico del sector público (Ley 40/2015)',
      'Órganos administrativos: principios',
      'Funcionamiento electrónico',
      'Convenios entre AAPP',
      'Responsabilidad patrimonial de la Administración',
    ],
    normativa: ['Ley 40/2015 arts. 1-50', 'Ley 40/2015 arts. 32-37', 'CE art. 106.2'],
  },
  'iipp-17': {
    index: [
      'Concepto y principios presupuestarios',
      'Estructura del presupuesto: orgánica, económica, programas',
      'Ciclo presupuestario',
      'Ejecución y modificaciones',
      'Estabilidad presupuestaria',
    ],
    normativa: ['Ley 47/2003 (LGP) arts. 26-37', 'LO 2/2012 estabilidad'],
  },
  // M2 — Derecho Penal
  'iipp-18': {
    index: [
      'Concepto y caracteres del Derecho Penal',
      'Principios limitadores del ius puniendi',
      'Aplicación de la ley penal en tiempo y espacio',
      'Estructura del Código Penal',
      'Garantías penales',
    ],
    normativa: ['CE art. 25', 'CP arts. 1-9', 'CP arts. 10-18'],
  },
  'iipp-19': {
    index: [
      'Concepto y elementos del delito',
      'Clases: delito grave, menos grave, leve',
      'Grados de ejecución (consumación, tentativa)',
      'Autoría y participación',
      'Concurso de delitos',
    ],
    normativa: ['CP arts. 10-18', 'CP arts. 27-31', 'CP arts. 73-78'],
  },
  'iipp-20': {
    index: [
      'Personas criminalmente responsables',
      'Autores, cómplices, encubridores',
      'Responsabilidad penal de personas jurídicas',
      'Responsabilidad civil derivada del delito',
      'Causas de exención y atenuación',
    ],
    normativa: ['CP arts. 27-31 bis', 'CP arts. 19-26', 'CP arts. 109-126'],
  },
  'iipp-21': {
    index: [
      'Suspensión ordinaria de la ejecución',
      'Suspensión por enfermedad muy grave',
      'Suspensión sustitutiva (drogodependientes)',
      'Sustitución de penas',
      'Revocación y remisión',
    ],
    normativa: ['CP arts. 80-87', 'CP arts. 88-89', 'LOGP arts. 71-73'],
  },
  'iipp-22': {
    index: [
      'Delitos contra la vida y la integridad física',
      'Delitos contra la libertad y la indemnidad sexual',
      'Delitos contra el patrimonio',
      'Delitos contra la salud pública',
      'Delitos contra el orden público',
    ],
    normativa: ['CP arts. 138-156', 'CP arts. 163-194', 'CP arts. 234-304', 'CP arts. 359-385', 'CP arts. 544-580'],
  },
  'iipp-23': {
    index: [
      'Bien jurídico protegido',
      'Prevaricación administrativa',
      'Cohecho, tráfico de influencias',
      'Malversación',
      'Quebrantamiento de condena',
    ],
    normativa: ['CP arts. 404-445', 'CP arts. 468-471 bis'],
  },
  'iipp-24': {
    index: [
      'Concepto de jurisdicción penal',
      'Competencia objetiva, funcional y territorial',
      'Órganos jurisdiccionales penales',
      'Partes en el proceso penal',
      'Acción penal pública y privada',
    ],
    normativa: ['LOPJ arts. 23-25', 'LECrim arts. 14-25', 'LECrim arts. 100-117'],
  },
  'iipp-25': {
    index: [
      'Estructura del procedimiento ordinario',
      'Fase de instrucción (sumario)',
      'Fase intermedia',
      'Juicio oral y sentencia',
      'Recursos',
    ],
    normativa: ['LECrim arts. 259-358', 'LECrim arts. 622-749'],
  },
  // M3 — Derecho Penitenciario
  'iipp-26': {
    index: [
      'Reglas Mínimas para el Tratamiento de los Reclusos (ONU 1955/2015 — Mandela)',
      'Reglas Penitenciarias Europeas (CoE 2006)',
      'Convenio Europeo para la Prevención de la Tortura',
      'Pacto Internacional Derechos Civiles y Políticos (art. 10)',
      'CEDH y jurisprudencia TEDH',
    ],
    normativa: ['Reglas Mandela (ONU)', 'Recomendación CM/Rec(2006)2', 'CEDH arts. 3 y 6'],
  },
  'iipp-27': {
    index: [
      'Concepto y naturaleza del Derecho Penitenciario',
      'Fuentes y principios informadores',
      'La LOGP 1/1979: estructura',
      'El Reglamento Penitenciario (RD 190/1996)',
      'Relaciones con otras ramas del Derecho',
    ],
    normativa: ['CE art. 25.2', 'LOGP arts. 1-3', 'RP arts. 1-5'],
  },
  'iipp-28': {
    index: [
      'Naturaleza de la relación jurídico-penitenciaria',
      'Derechos del interno (información, defensa, intimidad)',
      'Deberes del interno',
      'Sistema de garantías',
      'Quejas y peticiones',
    ],
    normativa: ['LOGP arts. 3-5', 'RP arts. 4-7', 'CE art. 25.2'],
  },
  'iipp-29': {
    index: [
      'Asistencia sanitaria',
      'Asistencia religiosa y cultural',
      'Acción social penitenciaria',
      'Educación y formación',
      'Trabajo penitenciario',
    ],
    normativa: ['LOGP arts. 21-58', 'RP arts. 207-230', 'RD 782/2001'],
  },
  'iipp-30': {
    index: [
      'Concepto de régimen penitenciario',
      'Régimen ordinario (art. 76 RP)',
      'Régimen abierto (CIS)',
      'Régimen cerrado y departamentos especiales',
      'Régimen de preventivos',
    ],
    normativa: ['LOGP arts. 9-10', 'RP arts. 73-100'],
  },
  'iipp-31': {
    index: [
      'Establecimientos preventivos, de cumplimiento y especiales',
      'Centros de Inserción Social (CIS)',
      'Unidades de madres y mixtas',
      'Departamentos psiquiátricos',
      'Hospitales penitenciarios',
    ],
    normativa: ['LOGP arts. 7-12', 'RP arts. 11-15'],
  },
  'iipp-32': {
    index: [
      'Concepto y características del régimen cerrado',
      'Aplicación: arts. 89-93 RP',
      'Modalidades: módulo y departamento especial',
      'Garantías y revisión',
      'Régimen de vida',
    ],
    normativa: ['LOGP art. 10', 'RP arts. 89-95'],
  },
  'iipp-33': {
    index: [
      'Concepto y finalidad del tratamiento',
      'Principios y elementos',
      'Junta de Tratamiento y Equipo Técnico',
      'Programas individualizados (PIT)',
      'Evaluación y clasificación interior',
    ],
    normativa: ['LOGP arts. 59-72', 'RP arts. 110-117', 'RP arts. 269-273'],
  },
  'iipp-34': {
    index: [
      'Naturaleza de la relación laboral especial penitenciaria',
      'OATPFE: organización y funciones',
      'Derechos laborales del interno',
      'Salario y cotización a la Seguridad Social',
      'Prevención de riesgos laborales',
    ],
    normativa: ['LOGP arts. 26-35', 'RD 782/2001', 'RP arts. 132-152'],
  },
  'iipp-35': {
    index: [
      'Permisos ordinarios y extraordinarios',
      'Requisitos legales y reglamentarios',
      'Procedimiento de concesión',
      'Revocación',
      'Salidas programadas',
    ],
    normativa: ['LOGP arts. 47-48', 'RP arts. 154-162'],
  },
  'iipp-36': {
    index: [
      'Libertad definitiva',
      'Libertad condicional: requisitos y modalidades',
      'Excarcelación por cumplimiento, indulto, amnistía',
      'Procedimiento de excarcelación',
      'Suspensión de la ejecución',
    ],
    normativa: ['CP arts. 90-92', 'LOGP arts. 17 y 72.6', 'RP arts. 192-201'],
  },
  'iipp-37': {
    index: [
      'Cumplimiento del art. 100.2 RP (modelo individualizado)',
      'Unidades dependientes',
      'Establecimientos militares',
      'Cumplimiento en hospital psiquiátrico penitenciario',
      'Internos extranjeros',
    ],
    normativa: ['RP arts. 165-184', 'CP art. 89 (expulsión)'],
  },
  'iipp-38': {
    index: [
      'Faltas: muy graves, graves y leves',
      'Sanciones aplicables',
      'Procedimiento sancionador',
      'Recursos ante el JVP',
      'Cumplimiento, suspensión y prescripción',
    ],
    normativa: ['LOGP arts. 41-45', 'RP arts. 231-262'],
  },
  'iipp-39': {
    index: [
      'Funciones del Juez de Vigilancia Penitenciaria',
      'Competencias en materia de tratamiento y régimen',
      'Recursos contra resoluciones administrativas',
      'Acumulación y refundición de condenas',
      'Estatuto orgánico',
    ],
    normativa: ['LOGP arts. 76-78', 'LOPJ arts. 94-95'],
  },
  'iipp-40': {
    index: [
      'Estructura organizativa de los CCPP',
      'Órganos colegiados (Consejo de Dirección, Junta de Tratamiento)',
      'Órganos unipersonales (Director, Subdirectores)',
      'Comisión Disciplinaria',
      'Áreas funcionales',
    ],
    normativa: ['RP arts. 263-276'],
  },
  'iipp-41': {
    index: [
      'Ingreso, conducciones y traslados',
      'Información, peticiones y quejas',
      'Comunicaciones y visitas',
      'Recompensas',
      'Higiene, alimentación y vestuario',
    ],
    normativa: ['LOGP arts. 15-25', 'RP arts. 15-72'],
  },
  'iipp-42': {
    index: [
      'Régimen económico-administrativo',
      'Peculio del interno',
      'Economatos y cuentas',
      'Contratación y suministros',
      'Control y fiscalización',
    ],
    normativa: ['RP arts. 318-323', 'Ley 47/2003 LGP'],
  },
  // M4 — Conducta Humana
  'iipp-43': {
    index: [
      'Estímulos y respuestas',
      'Refuerzo y castigo',
      'Aprendizaje social',
      'Personalidad y conducta',
      'Modificación de conducta',
    ],
    normativa: ['Bibliografía oficial CEAC — Psicología penitenciaria'],
  },
  'iipp-44': {
    index: [
      'Subcultura carcelaria',
      'Códigos internos y jerarquías',
      'Roles y grupos en prisión',
      'Conflictos y mediación',
      'Programas de intervención',
    ],
    normativa: ['LOGP art. 71', 'Bibliografía SGIP'],
  },
  'iipp-45': {
    index: [
      'El comportamiento social del interno',
      'Efectos psicológicos del internamiento',
      'Adaptación y prisionización',
      'Programas de habilidades sociales',
      'Reinserción comunitaria',
    ],
    normativa: ['CE art. 25.2', 'LOGP art. 1', 'RP arts. 116-117'],
  },
};

/**
 * Mapeo IIPP CEAC 2026 (45 temas) ↔ índice externo (50 temas).
 * Permite consultar equivalencias para test/casos cuando el usuario aporta
 * referencias del índice de 50 (parte I/II-A/II-B/III).
 */
export const IIPP_CEAC_TO_EXTERNAL_50: Record<number, number[]> = {
  1: [1, 2, 3, 4, 5],     // CE → temas 1-5 del externo
  2: [4],                  // Poder Judicial
  3: [4],                  // Gobierno
  4: [6],                  // Org. Territorial
  5: [8],                  // UE
  6: [7],                  // M. Interior y AGE
  7: [7, 13],              // Personal IIPP
  8: [13, 14],             // TREBEP
  9: [13],                 // Acceso empleo público
  10: [12],                // Contratos
  11: [],                  // Voluntariado (no figura explícito en externo)
  12: [],                  // Políticas públicas
  13: [],                  // Gobierno abierto
  14: [9, 10],             // Actividad AAPP
  15: [9],                 // Fuentes Dº Admvo
  16: [11],                // Procedimiento
  17: [17],                // Presupuesto
  18: [18],                // Dº Penal
  19: [19, 20],            // Delitos
  20: [20],                // Personas responsables
  21: [22],                // Suspensión penas
  22: [24, 25, 26],        // Principales delitos
  23: [26],                // Delitos contra AP
  24: [27],                // Jurisdicción penal
  25: [27],                // Procedimiento ordinario
  26: [33],                // Regulación supranacional
  27: [28],                // Dº Penitenciario
  28: [29, 31],            // Relación jurídico-penit.
  29: [32],                // Prestaciones
  30: [41, 42],            // Régimen
  31: [40],                // Tipos de establecimientos
  32: [41],                // Régimen cerrado
  33: [44],                // Tratamiento
  34: [46],                // Trabajo penitenciario
  35: [38],                // Permisos
  36: [34, 45],            // Libertad/excarcelación
  37: [42],                // Formas especiales
  38: [39],                // Disciplinario
  39: [45],                // Juez de Vigilancia
  40: [40],                // Modelo organizativo
  41: [40, 41],            // Régimen administrativo
  42: [],                  // Régimen económico
  43: [48],                // Conducta humana
  44: [50],                // Organización social prisión
  45: [49, 50],            // Comportamiento social
};

// ───────────────────────────── GUARDIA CIVIL (25 temas) ─────────────────────────────
export const ENRICHMENT_GC: Record<string, TopicEnrichment> = {
  'gc-1': {
    index: [
      'DUDH 1948',
      'PIDCP y PIDESC 1966',
      'CEDH 1950 y jurisprudencia TEDH',
      'Carta de Derechos Fundamentales UE',
      'Mecanismo Nacional de Prevención de la Tortura',
    ],
    normativa: ['DUDH', 'CEDH', 'Carta DFUE', 'CE arts. 10-55'],
  },
  'gc-2': {
    index: [
      'Principios generales de la igualdad',
      'Discriminación directa e indirecta',
      'Acoso por razón de sexo',
      'Planes de igualdad',
      'Tutela y garantías',
    ],
    normativa: ['LO 3/2007 igualdad', 'CE arts. 14 y 9.2', 'RDL 6/2019'],
  },
  'gc-3': {
    index: [
      'Derechos y deberes en PRL',
      'Servicios de prevención',
      'Evaluación de riesgos y plan de prevención',
      'Vigilancia de la salud',
      'Régimen sancionador',
    ],
    normativa: ['Ley 31/1995 PRL', 'RD 39/1997 RSP', 'RDL 5/2000'],
  },
  'gc-4': {
    index: [
      'Estructura y principios de la CE',
      'Derechos fundamentales y libertades públicas',
      'La Corona, Cortes Generales, Gobierno y Poder Judicial',
      'Organización territorial del Estado',
      'El Defensor del Pueblo (LO 3/1981)',
    ],
    normativa: ['CE arts. 1-169', 'LO 3/1981 Defensor del Pueblo'],
  },
  'gc-5': {
    index: [
      'Tratados constitutivos (TUE/TFUE)',
      'Instituciones: PE, Consejo, Comisión, TJUE',
      'Derecho originario y derivado (reglamento, directiva)',
      'Principios: primacía y efecto directo',
      'Espacio Schengen y libre circulación',
    ],
    normativa: ['TUE', 'TFUE', 'Acervo Schengen'],
  },
  'gc-6': {
    index: [
      'ONU: estructura y órganos',
      'OTAN y misiones',
      'Consejo de Europa y CEDH',
      'OSCE',
      'Interpol y Europol',
    ],
    normativa: ['Carta ONU', 'Tratado del Atlántico Norte 1949', 'Estatuto del CoE'],
  },
  'gc-7': {
    index: [
      'Fuentes del Derecho Civil',
      'La persona física y jurídica',
      'Capacidad jurídica y de obrar',
      'Obligaciones y contratos',
      'Derechos reales y propiedad',
    ],
    normativa: ['Código Civil arts. 1-7', 'CC arts. 29-39', 'CC arts. 1088-1314'],
  },
  'gc-8': {
    index: [
      'Concepto y caracteres del Derecho Penal',
      'Tipos de delitos según el CP',
      'Penas: clases, naturaleza y duración',
      'Medidas de seguridad',
      'Responsabilidad civil derivada del delito',
    ],
    normativa: ['CP arts. 1-18', 'CP arts. 32-60', 'CP arts. 95-108'],
  },
  'gc-9': {
    index: [
      'Jurisdicción y competencia penal',
      'La denuncia y la querella',
      'La detención y el habeas corpus',
      'Policía Judicial: funciones',
      'El atestado policial',
    ],
    normativa: ['LECrim arts. 259-298', 'LECrim arts. 489-501', 'LO 6/1984 habeas corpus'],
  },
  'gc-10': {
    index: [
      'Concepto y fuentes del Derecho Administrativo',
      'El acto administrativo y su eficacia',
      'Procedimiento administrativo común',
      'Recursos administrativos',
      'Responsabilidad patrimonial',
    ],
    normativa: ['Ley 39/2015 (LPACAP)', 'Ley 40/2015 (LRJSP) arts. 32-37'],
  },
  'gc-11': {
    index: [
      'Principios del RGPD',
      'Derechos del interesado (ARSULIPO)',
      'Responsable y encargado del tratamiento',
      'Brechas de seguridad y notificación',
      'AEPD: funciones',
    ],
    normativa: ['RGPD (UE) 2016/679', 'LO 3/2018 (LOPDGDD)'],
  },
  'gc-12': {
    index: [
      'Régimen general de extranjería',
      'Entrada, residencia y trabajo',
      'Reagrupación familiar',
      'Infracciones y sanciones',
      'Procedimiento de expulsión y devolución',
    ],
    normativa: ['LO 4/2000 extranjería', 'RD 557/2011 reglamento'],
  },
  'gc-13': {
    index: [
      'Distribución competencial Estado-CCAA-EELL',
      'LO 4/2015 protección seguridad ciudadana',
      'Documentación e identificación',
      'Régimen sancionador',
      'Seguridad privada (Ley 5/2014)',
    ],
    normativa: ['LO 4/2015', 'Ley 5/2014 seguridad privada', 'CE art. 149.1.29'],
  },
  'gc-14': {
    index: [
      'Estructura del Ministerio del Interior',
      'Secretaría de Estado de Seguridad',
      'Ministerio de Defensa: estructura',
      'Coordinación entre departamentos',
      'Dependencias de la Guardia Civil',
    ],
    normativa: ['RD 734/2020 estructura M. Interior', 'RD 521/2020 estructura M. Defensa'],
  },
  'gc-15': {
    index: [
      'LO 2/1986: principios básicos de actuación',
      'Naturaleza militar y policial de la Guardia Civil',
      'Misiones y funciones',
      'Estructura orgánica',
      'Régimen de personal (Ley 29/2014) y disciplinario (LO 12/2007)',
    ],
    normativa: ['LO 2/1986', 'Ley 29/2014', 'LO 12/2007'],
  },
  'gc-16': {
    index: [
      'Sistema Nacional de Protección Civil',
      'Planes de emergencia',
      'Desarrollo sostenible y ODS',
      'Eficiencia energética',
      'Cambio climático',
    ],
    normativa: ['Ley 17/2015 SNPC', 'Ley 7/2021 cambio climático', 'Agenda 2030'],
  },
  'gc-17': {
    index: [
      'Hardware y software',
      'Sistemas operativos',
      'Redes y modelo TCP/IP',
      'Internet y servicios',
      'Ciberseguridad básica',
    ],
    normativa: ['ENS (RD 311/2022)', 'Ley 6/2020 servicios electrónicos confianza'],
  },
  'gc-18': {
    index: [
      'Conceptos de cartografía',
      'Escalas y representación',
      'Coordenadas geográficas y UTM',
      'Lectura de planos',
      'Orientación con brújula y GPS',
    ],
    normativa: ['Bibliografía oficial GC — Topografía'],
  },
  'gc-19': {
    index: [
      'Concepto de deontología',
      'Código europeo de ética policial',
      'Principios básicos de actuación (LO 2/1986)',
      'Uso proporcional de la fuerza',
      'Responsabilidad disciplinaria',
    ],
    normativa: ['LO 2/1986 art. 5', 'Recomendación Rec(2001)10 CoE', 'LO 12/2007'],
  },
  'gc-20': {
    index: [
      'Marco LO 5/2000 responsabilidad menores',
      'Edad penal: 14-18 años',
      'Medidas aplicables',
      'Procedimiento ante el Juez de Menores',
      'Actuación policial con menores',
    ],
    normativa: ['LO 5/2000', 'CP art. 19'],
  },
  'gc-21': {
    index: [
      'Concepto de violencia de género (LO 1/2004)',
      'Medidas de protección integral',
      'Orden de protección (art. 544 ter LECrim)',
      'Atestado y denuncia',
      'VIOGEN y coordinación policial',
    ],
    normativa: ['LO 1/2004', 'CP arts. 153, 171.4, 172.2', 'LECrim art. 544 ter'],
  },
  'gc-22': {
    index: [
      'Reglamento de Armas (RD 137/1993)',
      'Categorías de armas',
      'Licencias y autorizaciones',
      'Reglamento de Explosivos (RD 130/2017)',
      'Tenencia, depósito y tráfico ilícito',
    ],
    normativa: ['RD 137/1993 Reglamento de Armas', 'RD 130/2017 Reglamento de Explosivos', 'CP arts. 563-570'],
  },
  'gc-23': {
    index: [
      'Concepto de aduana y régimen aduanero',
      'Código Aduanero de la Unión',
      'Delito de contrabando (LO 12/1995)',
      'Procedimiento sancionador aduanero',
      'Resguardo Fiscal del Estado',
    ],
    normativa: ['Reglamento (UE) 952/2013', 'LO 12/1995 contrabando', 'Ley 58/2003 LGT'],
  },
  'gc-24': {
    index: [
      'Comprensión lectora B1',
      'Vocabulario operativo policial',
      'Estructuras gramaticales básicas',
      'Expresión escrita formal',
      'Listening contextual',
    ],
    normativa: ['MCERL B1 — Consejo de Europa'],
  },
  'gc-25': {
    index: [
      'Reglas de acentuación',
      'Uso de mayúsculas y signos de puntuación',
      'Categorías gramaticales',
      'Sintaxis de la oración',
      'Errores frecuentes',
    ],
    normativa: ['Ortografía RAE 2010', 'NGLE 2009-2011'],
  },
};

// ───────────────────────────── C1 ADMINISTRATIVO (47 temas) ─────────────────────────────
export const ENRICHMENT_C1: Record<string, TopicEnrichment> = {
  'c1n-1': {
    index: [
      'Estructura y principios de la CE',
      'Título Preliminar y valores superiores',
      'Derechos fundamentales (Tít. I)',
      'Garantías (art. 53) y suspensión (art. 55)',
      'Reforma constitucional',
    ],
    normativa: ['CE arts. 1-9', 'CE arts. 14-29', 'CE arts. 53-55', 'CE arts. 166-169'],
  },
  'c1n-2': {
    index: [
      'La Corona: funciones (art. 56)',
      'Sucesión y regencia',
      'Refrendo y responsabilidad',
      'Casa del Rey',
      'Actos del Rey',
    ],
    normativa: ['CE arts. 56-65'],
  },
  'c1n-3': {
    index: [
      'Composición del Congreso y Senado',
      'Estatuto de diputados y senadores',
      'Procedimiento legislativo',
      'Funciones de control',
      'Disolución de las Cámaras',
    ],
    normativa: ['CE arts. 66-96', 'RC y RS'],
  },
  'c1n-4': {
    index: [
      'Principios del Poder Judicial',
      'CGPJ: composición y competencias',
      'Tribunal Supremo y AN',
      'Organización judicial',
      'Ministerio Fiscal',
    ],
    normativa: ['CE arts. 117-127', 'LOPJ 6/1985'],
  },
  'c1n-5': {
    index: [
      'Composición del Gobierno',
      'Presidente: nombramiento y cese',
      'Consejo de Ministros',
      'Comisiones Delegadas',
      'Relaciones con las Cortes',
    ],
    normativa: ['CE arts. 97-107', 'Ley 50/1997 del Gobierno'],
  },
  'c1n-6': {
    index: [
      'Concepto de Gobierno Abierto',
      'Pilares: transparencia, participación, colaboración',
      'Agenda 2030 y los 17 ODS',
      'Datos abiertos',
      'Plan Nacional Gobierno Abierto',
    ],
    normativa: ['Ley 19/2013', 'Resolución ONU A/RES/70/1 (Agenda 2030)'],
  },
  'c1n-7': {
    index: [
      'Ámbito subjetivo y objetivo de la Ley 19/2013',
      'Publicidad activa',
      'Derecho de acceso a la información pública',
      'Consejo de Transparencia y Buen Gobierno',
      'Buen gobierno: obligaciones',
    ],
    normativa: ['Ley 19/2013', 'RD 919/2014 estatuto CTBG'],
  },
  'c1n-8': {
    index: [
      'Organización central: Ministerios',
      'Órganos superiores y directivos',
      'Administración Periférica (Delegados/Subdelegados)',
      'Administración General del Estado en el exterior',
      'Sector público institucional',
    ],
    normativa: ['Ley 40/2015 arts. 54-94', 'RD 466/2025 estructura'],
  },
  'c1n-9': {
    index: [
      'Las CCAA: principios constitucionales',
      'Estatutos de Autonomía',
      'Distribución de competencias',
      'Conflictos de competencias',
      'Financiación autonómica',
    ],
    normativa: ['CE arts. 137-158', 'LOFCA 8/1980'],
  },
  'c1n-10': {
    index: [
      'Provincias y municipios',
      'Órganos de gobierno locales',
      'Competencias locales',
      'Servicios mínimos obligatorios',
      'Régimen especial: Madrid, Barcelona',
    ],
    normativa: ['Ley 7/1985 (LBRL)', 'TRRL (RDL 781/1986)'],
  },
  'c1n-11': {
    index: [
      'Proceso de integración europea',
      'Tratados (TUE, TFUE)',
      'Instituciones de la UE',
      'Fuentes del Derecho UE',
      'Fondos europeos',
    ],
    normativa: ['TUE', 'TFUE'],
  },
  'c1n-12': {
    index: [
      'Información general y particular',
      'Calidad y carta de servicios',
      'Quejas y sugerencias',
      'Atención a personas con discapacidad',
      'Lenguaje accesible',
    ],
    normativa: ['RD 951/2005', 'RDL 1/2013 discapacidad'],
  },
  'c1n-13': {
    index: [
      'Documento administrativo',
      'Registro electrónico',
      'Archivo: clases (gestión, central, intermedio, histórico)',
      'Conservación y eliminación',
      'Patrimonio documental',
    ],
    normativa: ['Ley 39/2015 art. 16', 'Ley 16/1985 PHE', 'NTI archivo electrónico'],
  },
  'c1n-14': {
    index: [
      'Sede electrónica',
      'Punto de Acceso General',
      'Notificaciones electrónicas',
      'Identificación y firma electrónica (Cl@ve)',
      'Interoperabilidad (ENI)',
    ],
    normativa: ['Ley 39/2015 arts. 9-17', 'RD 311/2022 ENS', 'RD 4/2010 ENI'],
  },
  'c1n-15': {
    index: [
      'Principios del RGPD',
      'Derechos del interesado',
      'Responsable y encargado',
      'Brechas y notificación a la AEPD',
      'Régimen sancionador',
    ],
    normativa: ['RGPD (UE) 2016/679', 'LO 3/2018 (LOPDGDD)'],
  },
  'c1n-16': {
    index: [
      'Constitución, ley, reglamento',
      'Tratados internacionales',
      'Derecho de la UE',
      'Costumbre y principios generales',
      'Jerarquía normativa',
    ],
    normativa: ['CE art. 9.3', 'Código Civil arts. 1-7'],
  },
  'c1n-17': {
    index: [
      'Concepto y elementos del acto',
      'Clases de actos administrativos',
      'Motivación y forma',
      'Eficacia, notificación y publicación',
      'Validez: nulidad y anulabilidad',
    ],
    normativa: ['Ley 39/2015 arts. 34-52'],
  },
  'c1n-18': {
    index: [
      'Iniciación, ordenación e instrucción',
      'Terminación: resolución, desistimiento, caducidad',
      'Notificación y plazos',
      'Procedimientos especiales',
      'Régimen jurídico del sector público',
    ],
    normativa: ['Ley 39/2015 arts. 53-105', 'Ley 40/2015 arts. 1-50'],
  },
  'c1n-19': {
    index: [
      'Tipos de contratos públicos',
      'Procedimientos de adjudicación',
      'Capacidad y solvencia',
      'Ejecución y modificación',
      'Recurso especial en contratación',
    ],
    normativa: ['Ley 9/2017 (LCSP) arts. 12-25', 'LCSP arts. 130-187', 'LCSP arts. 195-246'],
  },
  'c1n-20': {
    index: [
      'Actividad de policía, fomento y servicio público',
      'Formas de gestión directa e indirecta',
      'Subvenciones (Ley 38/2003)',
      'Convenios entre AAPP',
      'Encomiendas y encargos',
    ],
    normativa: ['Ley 38/2003 General de Subvenciones', 'Ley 40/2015 arts. 47-53'],
  },
  'c1n-21': {
    index: [
      'Fundamento constitucional (art. 106.2)',
      'Requisitos de la responsabilidad',
      'Procedimiento',
      'Responsabilidad concurrente',
      'Acción de regreso',
    ],
    normativa: ['CE art. 106.2', 'Ley 40/2015 arts. 32-37', 'Ley 39/2015 arts. 65 y 67'],
  },
  'c1n-22': {
    index: [
      'LO 3/2007 igualdad',
      'LO 1/2004 violencia de género',
      'Ley 4/2023 LGTBI',
      'RDL 1/2013 discapacidad',
      'Ley 39/2006 dependencia',
    ],
    normativa: ['LO 3/2007', 'LO 1/2004', 'Ley 4/2023', 'RDL 1/2013', 'Ley 39/2006'],
  },
  'c1n-23': {
    index: [
      'Clases de personal (TREBEP art. 8)',
      'Funcionarios de carrera, interinos, eventuales',
      'Personal laboral',
      'Estructura: grupos y subgrupos',
      'Normativa aplicable',
    ],
    normativa: ['TREBEP arts. 8-13', 'TREBEP arts. 76-77'],
  },
  'c1n-24': {
    index: [
      'Principios del acceso al empleo público',
      'Oferta de Empleo Público',
      'Sistemas selectivos',
      'Órganos de selección',
      'Provisión de puestos',
    ],
    normativa: ['TREBEP arts. 55-62', 'TREBEP arts. 78-84', 'RD 364/1995'],
  },
  'c1n-25': {
    index: [
      'Adquisición de la condición de funcionario',
      'Derechos individuales',
      'Derechos colectivos',
      'Deberes y código de conducta',
      'Carrera profesional',
    ],
    normativa: ['TREBEP arts. 14-23', 'TREBEP arts. 52-54'],
  },
  'c1n-26': {
    index: [
      'Adquisición y pérdida de la condición',
      'Servicio activo',
      'Servicios especiales',
      'Excedencias (voluntaria, forzosa, cuidado familiares)',
      'Suspensión de funciones',
    ],
    normativa: ['TREBEP arts. 55-68', 'TREBEP arts. 85-92'],
  },
  'c1n-27': {
    index: [
      'Sistemas de provisión: concurso y libre designación',
      'Carrera horizontal y vertical',
      'Promoción interna',
      'Movilidad voluntaria',
      'Evaluación del desempeño',
    ],
    normativa: ['TREBEP arts. 16-20', 'TREBEP arts. 78-84'],
  },
  'c1n-28': {
    index: [
      'Régimen de incompatibilidades (Ley 53/1984)',
      'Faltas: muy graves, graves y leves',
      'Sanciones aplicables',
      'Procedimiento disciplinario',
      'Prescripción',
    ],
    normativa: ['Ley 53/1984 incompatibilidades', 'TREBEP arts. 93-98'],
  },
  'c1n-29': {
    index: [
      'MUFACE: prestaciones',
      'Clases pasivas: jubilación, viudedad, orfandad',
      'Acción protectora',
      'Cotización',
      'Procedimientos',
    ],
    normativa: ['RDL 4/2000 MUFACE', 'RDL 670/1987 Clases Pasivas'],
  },
  'c1n-30': {
    index: [
      'IV Convenio Único AGE',
      'Categorías profesionales',
      'Condiciones de trabajo',
      'Estructura retributiva',
      'Régimen disciplinario laboral',
    ],
    normativa: ['IV Convenio Único AGE (BOE 2019)', 'ET (RDL 2/2015)'],
  },
  'c1n-31': {
    index: [
      'Régimen General de la Seguridad Social',
      'Afiliación, altas y bajas',
      'Cotización',
      'Acción protectora',
      'Prestaciones contributivas',
    ],
    normativa: ['LGSS (RDL 8/2015)'],
  },
  'c1n-32': {
    index: [
      'Concepto y principios presupuestarios',
      'Clasificación orgánica, económica y por programas',
      'Estabilidad presupuestaria',
      'Ciclo presupuestario',
      'Régimen jurídico LGP',
    ],
    normativa: ['Ley 47/2003 (LGP) arts. 26-37', 'LO 2/2012 estabilidad'],
  },
  'c1n-33': {
    index: [
      'Estructura de los PGE',
      'Ciclo: elaboración, aprobación, ejecución, control',
      'Plurianualidad',
      'Estado de gastos e ingresos',
      'Anexos',
    ],
    normativa: ['Ley 47/2003 arts. 26-78', 'CE art. 134'],
  },
  'c1n-34': {
    index: [
      'Fases del gasto: ADOK',
      'Modificaciones presupuestarias',
      'Cuenta General del Estado',
      'Tribunal de Cuentas',
      'IGAE',
    ],
    normativa: ['Ley 47/2003 arts. 73-110', 'LO 2/1982 Tribunal de Cuentas'],
  },
  'c1n-35': {
    index: [
      'Retribuciones básicas y complementarias',
      'Trienios y antigüedad',
      'Pagas extraordinarias',
      'Indemnizaciones por razón del servicio',
      'Gestión de nóminas',
    ],
    normativa: ['TREBEP arts. 21-30', 'RD 462/2002 indemnizaciones'],
  },
  'c1n-36': {
    index: [
      'Pagos en firme',
      'Anticipos a justificar',
      'Pagos a justificar',
      'Justificación y control',
      'Caja Pagadora',
    ],
    normativa: ['Ley 47/2003 arts. 73-79', 'OEHA órdenes de pago'],
  },
  'c1n-37': {
    index: [
      'Gestión económica de contratos',
      'Subvenciones: concesión y justificación',
      'Reintegros',
      'Convenios',
      'Encargos a medios propios',
    ],
    normativa: ['LCSP 9/2017', 'Ley 38/2003 General de Subvenciones'],
  },
  'c1n-38': {
    index: [
      'Hardware: CPU, memoria, periféricos',
      'Software: tipos y licencias',
      'Almacenamiento (HDD, SSD, nube)',
      'Seguridad informática básica',
      'Copias de seguridad',
    ],
    normativa: ['ENS (RD 311/2022)'],
  },
  'c1n-39': {
    index: [
      'Características de Windows 11',
      'Escritorio, menú Inicio, barra de tareas',
      'Configuración del sistema',
      'Seguridad y cuentas',
      'Microsoft Copilot integrado',
    ],
    normativa: ['Microsoft Windows 11 — documentación oficial'],
  },
  'c1n-40': {
    index: [
      'Estructura de carpetas y archivos',
      'Operaciones: copiar, mover, renombrar',
      'Búsqueda avanzada',
      'Compresión y descompresión',
      'Permisos y propiedades',
    ],
    normativa: ['Microsoft Windows 11 — documentación oficial'],
  },
  'c1n-41': {
    index: [
      'Edición y formato de texto',
      'Estilos y plantillas',
      'Tablas, imágenes, encabezados',
      'Combinación de correspondencia',
      'Revisión y comentarios',
    ],
    normativa: ['Microsoft Word 365 — documentación oficial'],
  },
  'c1n-42': {
    index: [
      'Estructura de hojas y libros',
      'Fórmulas y operadores',
      'Funciones (lógicas, búsqueda, fecha, texto)',
      'Tablas dinámicas y gráficos',
      'Copilot en Excel',
    ],
    normativa: ['Microsoft Excel 365 — documentación oficial'],
  },
  'c1n-43': {
    index: [
      'Concepto de base de datos relacional',
      'Tablas, campos y relaciones',
      'Consultas y formularios',
      'Informes',
      'Macros básicas',
    ],
    normativa: ['Microsoft Access 365 — documentación oficial'],
  },
  'c1n-44': {
    index: [
      'Gestión de correo electrónico',
      'Calendario y citas',
      'Contactos y tareas',
      'Reglas y filtros',
      'Firma digital y cifrado',
    ],
    normativa: ['Microsoft Outlook 365 — documentación oficial'],
  },
  'c1n-45': {
    index: [
      'Origen y evolución de Internet',
      'Protocolos TCP/IP, HTTP/HTTPS',
      'Servicios: web, correo, FTP, DNS',
      'Navegadores y buscadores',
      'Seguridad: phishing, malware',
    ],
    normativa: ['ENS (RD 311/2022)', 'Ley 6/2020 servicios electrónicos confianza'],
  },
  'c1n-46': {
    index: [
      'Principios de la LO 3/2007',
      'Discriminación directa e indirecta',
      'Planes de igualdad en la AGE',
      'LO 1/2004 violencia de género',
      'Ley 4/2023 LGTBI',
    ],
    normativa: ['LO 3/2007', 'LO 1/2004', 'Ley 4/2023'],
  },
  'c1n-47': {
    index: [
      'Marco PRL en la AGE',
      'Servicios de prevención propios',
      'Evaluación de riesgos',
      'Vigilancia de la salud',
      'Comités de seguridad y salud',
    ],
    normativa: ['Ley 31/1995 PRL', 'RD 39/1997 RSP', 'RD 67/2010 PRL en AGE'],
  },
};

// ───────────────────────────── C2 AUXILIAR (28 temas) ─────────────────────────────
export const ENRICHMENT_C2: Record<string, TopicEnrichment> = {
  'c2-1': {
    index: [
      'Estructura de la CE de 1978',
      'Derechos fundamentales y libertades públicas',
      'La Corona',
      'Cortes Generales',
      'Reforma constitucional',
    ],
    normativa: ['CE arts. 1-9', 'CE arts. 14-29', 'CE arts. 56-65', 'CE arts. 66-96'],
  },
  'c2-2': {
    index: [
      'Composición y elección del TC',
      'Competencias del TC',
      'Recurso de amparo',
      'Recurso y cuestión de inconstitucionalidad',
      'Conflictos de competencias',
    ],
    normativa: ['CE arts. 159-165', 'LOTC 2/1979'],
  },
  'c2-3': {
    index: [
      'Composición Congreso y Senado',
      'Funciones legislativa y de control',
      'Procedimiento legislativo',
      'Defensor del Pueblo',
      'Disolución de las Cámaras',
    ],
    normativa: ['CE arts. 66-96', 'LO 3/1981 Defensor del Pueblo'],
  },
  'c2-4': {
    index: [
      'Principios constitucionales',
      'CGPJ',
      'TS y AN',
      'Organización judicial española',
      'Ministerio Fiscal',
    ],
    normativa: ['CE arts. 117-127', 'LOPJ 6/1985'],
  },
  'c2-5': {
    index: [
      'Composición y funciones del Gobierno',
      'Presidente del Gobierno',
      'Consejo de Ministros',
      'Administración General del Estado',
      'Relaciones Gobierno-Cortes',
    ],
    normativa: ['CE arts. 97-107', 'Ley 50/1997', 'Ley 40/2015'],
  },
  'c2-6': {
    index: [
      'Concepto de Gobierno Abierto',
      'Pilares: transparencia, participación, colaboración',
      'Agenda 2030 y ODS',
      'Datos abiertos',
      'Plan Nacional Gobierno Abierto',
    ],
    normativa: ['Ley 19/2013', 'Resolución ONU A/RES/70/1'],
  },
  'c2-7': {
    index: [
      'Ámbito de aplicación de la Ley 19/2013',
      'Publicidad activa',
      'Derecho de acceso a la información',
      'Consejo de Transparencia y Buen Gobierno',
      'Régimen sancionador',
    ],
    normativa: ['Ley 19/2013'],
  },
  'c2-8': {
    index: [
      'Organización central: Ministerios',
      'Órganos superiores y directivos',
      'Administración Periférica',
      'AGE en el exterior',
      'Sector público institucional',
    ],
    normativa: ['Ley 40/2015 arts. 54-94'],
  },
  'c2-9': {
    index: [
      'Las CCAA: principios constitucionales',
      'Estatutos de Autonomía',
      'Distribución competencial',
      'Administración Local',
      'Financiación territorial',
    ],
    normativa: ['CE arts. 137-158', 'Ley 7/1985 (LBRL)'],
  },
  'c2-10': {
    index: [
      'Tratados constitutivos UE',
      'Instituciones europeas',
      'Derecho originario y derivado',
      'Principios: primacía y efecto directo',
      'Fondos europeos',
    ],
    normativa: ['TUE', 'TFUE'],
  },
  'c2-11': {
    index: [
      'Iniciación, ordenación, instrucción y terminación',
      'Notificación y plazos',
      'Recursos administrativos',
      'Régimen jurídico del sector público',
      'Responsabilidad patrimonial',
    ],
    normativa: ['Ley 39/2015', 'Ley 40/2015'],
  },
  'c2-12': {
    index: [
      'Principios del RGPD',
      'Derechos del interesado',
      'Responsable y encargado',
      'AEPD: funciones',
      'Régimen sancionador',
    ],
    normativa: ['RGPD (UE) 2016/679', 'LO 3/2018 (LOPDGDD)'],
  },
  'c2-13': {
    index: [
      'Concepto de funcionario público',
      'Clases de personal (TREBEP art. 8)',
      'Adquisición y pérdida de la condición',
      'Situaciones administrativas',
      'Provisión de puestos',
    ],
    normativa: ['TREBEP arts. 8-13', 'TREBEP arts. 55-68', 'TREBEP arts. 85-92'],
  },
  'c2-14': {
    index: [
      'Derechos individuales y colectivos',
      'Deberes y código de conducta',
      'Carrera administrativa',
      'Retribuciones',
      'Régimen disciplinario',
    ],
    normativa: ['TREBEP arts. 14-30', 'TREBEP arts. 52-54', 'TREBEP arts. 93-98'],
  },
  'c2-15': {
    index: [
      'Principios presupuestarios',
      'Estructura PGE',
      'Ciclo presupuestario',
      'Ejecución del gasto (ADOK)',
      'Control: IGAE y Tribunal de Cuentas',
    ],
    normativa: ['Ley 47/2003 (LGP)', 'CE art. 134'],
  },
  'c2-16': {
    index: [
      'LO 3/2007 igualdad',
      'LO 1/2004 violencia de género',
      'Planes de igualdad',
      'Tutela judicial',
      'Ley 4/2023 LGTBI',
    ],
    normativa: ['LO 3/2007', 'LO 1/2004', 'Ley 4/2023'],
  },
  'c2-17': {
    index: [
      'Información general y particular al ciudadano',
      'Calidad de servicio',
      'Atención presencial, telefónica y telemática',
      'Quejas y sugerencias',
      'Atención a personas con discapacidad',
    ],
    normativa: ['RD 951/2005', 'RDL 1/2013 discapacidad'],
  },
  'c2-18': {
    index: [
      'Servicios de información administrativa',
      'Oficinas integradas',
      'Punto de Acceso General electrónico',
      'Carta de servicios',
      'Coordinación interadministrativa',
    ],
    normativa: ['RD 208/1996', 'Ley 39/2015 art. 13'],
  },
  'c2-19': {
    index: [
      'Concepto de documento administrativo',
      'Funciones del registro electrónico',
      'Clases de archivo (gestión, central, intermedio, histórico)',
      'Conservación y eliminación',
      'Patrimonio documental',
    ],
    normativa: ['Ley 39/2015 art. 16', 'Ley 16/1985 PHE'],
  },
  'c2-20': {
    index: [
      'Sede electrónica',
      'Identificación y firma electrónica (Cl@ve)',
      'Notificaciones electrónicas',
      'Punto de Acceso General',
      'Interoperabilidad',
    ],
    normativa: ['Ley 39/2015 arts. 9-17', 'RD 311/2022 ENS'],
  },
  'c2-21': {
    index: [
      'Hardware: CPU, memoria, periféricos',
      'Software: sistemas, aplicaciones, licencias',
      'Almacenamiento',
      'Redes básicas',
      'Seguridad informática',
    ],
    normativa: ['ENS (RD 311/2022)'],
  },
  'c2-22': {
    index: [
      'Características de Windows 11',
      'Escritorio y menú Inicio',
      'Configuración y cuentas',
      'Seguridad',
      'Microsoft Copilot',
    ],
    normativa: ['Microsoft Windows 11 — documentación oficial'],
  },
  'c2-23': {
    index: [
      'Estructura de carpetas y archivos',
      'Operaciones de gestión',
      'Búsqueda',
      'Compresión',
      'Permisos',
    ],
    normativa: ['Microsoft Windows 11 — documentación oficial'],
  },
  'c2-24': {
    index: [
      'Edición y formato',
      'Estilos y plantillas',
      'Tablas e imágenes',
      'Combinación de correspondencia',
      'Revisión',
    ],
    normativa: ['Microsoft Word 365 — documentación oficial'],
  },
  'c2-25': {
    index: [
      'Hojas y libros',
      'Fórmulas y referencias',
      'Funciones básicas (SUMA, PROMEDIO, CONTAR.SI)',
      'Formato condicional',
      'Gráficos',
    ],
    normativa: ['Microsoft Excel 365 — documentación oficial'],
  },
  'c2-26': {
    index: [
      'Concepto de base de datos',
      'Tablas y campos',
      'Consultas',
      'Formularios',
      'Informes',
    ],
    normativa: ['Microsoft Access 365 — documentación oficial'],
  },
  'c2-27': {
    index: [
      'Configuración de cuentas',
      'Envío y recepción de mensajes',
      'Adjuntos',
      'Calendario y contactos',
      'Reglas y filtros',
    ],
    normativa: ['Microsoft Outlook 365 — documentación oficial'],
  },
  'c2-28': {
    index: [
      'Origen y evolución de Internet',
      'Protocolos TCP/IP',
      'Servicios: web, correo, FTP',
      'Navegadores',
      'Seguridad: phishing y malware',
    ],
    normativa: ['ENS (RD 311/2022)'],
  },
};

/** Aplica el enriquecimiento (overlay) a un array de temas. */
export function enrichTopics(
  topics: CuerpoTopic[],
  enrichment: Record<string, TopicEnrichment>,
): CuerpoTopic[] {
  return topics.map((t) => {
    const e = enrichment[t.id];
    if (!e) return t;
    const hasIndex = Array.isArray(t.index) && t.index.length > 0;
    const hasNormativa = Array.isArray(t.normativa) && t.normativa.length > 0;
    return {
      ...t,
      index: hasIndex ? t.index : e.index,
      normativa: hasNormativa ? t.normativa : e.normativa,
    };
  });
}
